import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { NotebookText, Plus, Trash2, Users, X, Loader2, ChevronRight, FileText, Check, Link2, CalendarClock, PartyPopper, Search, GripVertical } from 'lucide-react';
import { notesApi } from '../../lib/api';

const RichEditor = React.lazy(() => import('./RichEditor'));

// Lot 5 — écran du module Notes (fondations serveur : Lot 4, voir functions/api/notes.js).
//
// Simplifications assumées pour ce premier écran (voir aussi les commentaires de notes.js et
// RichEditor.tsx) :
//   - pas de glisser-déposer pour réordonner sections/pages ;
//   - pas de recherche plein texte (dépend de l'extraction de texte du format Tiptap) ;
//   - suppression avec confirmation, définitive — conforme au cahier des charges d'origine
//     sur ce point précis (les Notes n'ont jamais eu de Ctrl+Z, contrairement au ToDo) ;
//   - pas d'images ni de pièces jointes dans l'éditeur (voir RichEditor.tsx).

type Notebook = { id: string; name: string; user_id: string; mine: boolean };
type Section = { id: string; notebook_id: string; name: string };
type Page = {
    id: string; section_id: string; parent_page_id: string | null; title: string; updated_at: string;
    linked_appointment_id: string | null; linked_event_id: string | null;
    linked_appointment_label?: string | null; linked_appointment_date?: string | null; linked_appointment_time?: string | null;
    linked_event_label?: string | null; linked_event_type?: string | null; linked_event_start?: string | null;
};
type AppointmentCandidate = { id: string; prestation_label: string | null; appointment_date: string; start_time: string };
type EventCandidate = { id: string; type: string | null; first_name: string | null; last_name: string | null; company_name: string | null; start_time: string };

/** Nom affiché pour un événement : même règle que l'écran Événementiel lui-même — prénom+nom pour
 *  un client PRIVÉ, raison sociale sinon. */
const eventClientName = (e: { type: string | null; first_name: string | null; last_name: string | null; company_name: string | null }) =>
    e.type === 'PRIVÉ' ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : (e.company_name || '');

/** "2026-08-18" → "18/08/2026". */
const fmtDateFR = (isoDate: string) => {
    const [y, m, d] = (isoDate || '').split('-');
    return y && m && d ? `${d}/${m}/${y}` : isoDate;
};
/** "2026-08-01T19:00:00" → "01/08/2026 19:00". */
const fmtDateTimeFR = (iso: string) => {
    const [datePart, timePart] = (iso || '').split('T');
    return `${fmtDateFR(datePart)}${timePart ? ' ' + timePart.slice(0, 5) : ''}`;
};

/** Nom + date/heure du rendez-vous ou de l'événement rattaché à une page, prêt à afficher. */
const linkDisplay = (page: Page): string | null => {
    if (page.linked_appointment_id) {
        return `${page.linked_appointment_label || 'Rendez-vous'} — ${page.linked_appointment_date ? fmtDateFR(page.linked_appointment_date) : ''} ${page.linked_appointment_time || ''}`.trim();
    }
    if (page.linked_event_id) {
        return `${page.linked_event_label || page.linked_event_type || 'Événement'} — ${page.linked_event_start ? fmtDateTimeFR(page.linked_event_start) : ''}`.trim();
    }
    return null;
};
type Collaborator = { id: string; name: string; username: string };

const SAVE_DEBOUNCE_MS = 1200;

function PromptDialog({ title, placeholder, confirmLabel = 'Créer', initialValue = '', onSubmit, onClose }: {
    title: string; placeholder: string; confirmLabel?: string; initialValue?: string; onSubmit: (value: string) => void; onClose: () => void;
}) {
    const [value, setValue] = React.useState(initialValue);
    const inputRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
    const submit = () => {
        const trimmed = value.trim();
        if (!trimmed) return;
        onSubmit(trimmed);
        onClose();
    };
    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content">{title}</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-4">
                        <input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onClose(); }}
                            placeholder={placeholder}
                            className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2.5 text-sm text-content placeholder:text-content-muted outline-none focus:border-[var(--brand-color)]" />
                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm font-bold text-content-muted hover:text-content">Annuler</button>
                            <button onClick={submit} disabled={!value.trim()} className="px-4 py-2 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold disabled:opacity-40">{confirmLabel}</button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

function ShareDialog({ notebookId, name, onClose }: { notebookId: string; name: string; onClose: () => void }) {
    const [shares, setShares] = React.useState<Collaborator[]>([]);
    const [candidates, setCandidates] = React.useState<Collaborator[] | null>(null);
    const [owner, setOwner] = React.useState<Collaborator | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [picked, setPicked] = React.useState('');

    const load = React.useCallback(() => {
        setLoading(true);
        notesApi.getNotebookShares(notebookId)
            .then((r: any) => { setShares(r.shares || []); setCandidates(r.candidates); setOwner(r.owner); })
            .finally(() => setLoading(false));
    }, [notebookId]);
    React.useEffect(() => { load(); }, [load]);

    const handleAdd = async () => { if (!picked) return; await notesApi.shareNotebook(notebookId, picked); setPicked(''); load(); };
    const handleRemove = async (userId: string) => { await notesApi.unshareNotebook(notebookId, userId); load(); };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content flex items-center gap-2"><Users size={18} /> Partage de « {name} »</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
                        ) : (
                            <>
                                <div className="space-y-1.5">
                                    {owner && (
                                        <div className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg bg-surface-soft">
                                            <span className="text-content font-medium">{owner.name || owner.username}</span>
                                            <span className="text-[10px] font-bold uppercase text-content-muted">Propriétaire</span>
                                        </div>
                                    )}
                                    {shares.map((s) => (
                                        <div key={s.id} className="flex items-center justify-between text-sm py-1.5 px-3 rounded-lg">
                                            <span className="text-content">{s.name || s.username}</span>
                                            {candidates !== null && <button onClick={() => handleRemove(s.id)} className="text-content-muted hover:text-red-500"><X size={14} /></button>}
                                        </div>
                                    ))}
                                    {shares.length === 0 && <p className="text-xs text-content-muted px-3">Pas encore partagé.</p>}
                                </div>
                                {candidates !== null && (
                                    <div className="flex gap-2 pt-2 border-t border-border">
                                        <select value={picked} onChange={(e) => setPicked(e.target.value)}
                                            className="flex-1 bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-sm text-content">
                                            <option value="">Choisir un collègue…</option>
                                            {candidates.map((c) => <option key={c.id} value={c.id}>{c.name || c.username}</option>)}
                                        </select>
                                        <button onClick={handleAdd} disabled={!picked} className="px-3 py-2 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold disabled:opacity-40">Partager</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

/** Rattache une page à un rendez-vous de prestation ou à un événement du planning événementiel —
 *  jamais les deux (voir `normaliserRattachement` côté serveur). Demandé par Emeni le 2026-08-17,
 *  sur le même modèle que le rattachement (non exposé à l'écran) déjà décidé pour le ToDo. */
function LinkDialog({ page, onLink, onUnlink, onClose }: {
    page: Page; onLink: (kind: 'appointment' | 'event', item: AppointmentCandidate | EventCandidate) => void; onUnlink: () => void; onClose: () => void;
}) {
    const currentKind: 'appointment' | 'event' | null = page.linked_appointment_id ? 'appointment' : page.linked_event_id ? 'event' : null;
    const [kind, setKind] = React.useState<'appointment' | 'event'>(currentKind || 'appointment');
    const [query, setQuery] = React.useState('');
    const [results, setResults] = React.useState<(AppointmentCandidate | EventCandidate)[]>([]);
    const [loading, setLoading] = React.useState(false);
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        setLoading(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const search = kind === 'appointment' ? notesApi.searchAppointmentCandidates(query) : notesApi.searchEventCandidates(query);
            search.then((r: any) => setResults(kind === 'appointment' ? (r.appointments || []) : (r.events || []))).finally(() => setLoading(false));
        }, 250);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [kind, query]);

    const labelFor = (item: AppointmentCandidate | EventCandidate, k: 'appointment' | 'event') => k === 'appointment'
        ? `${(item as AppointmentCandidate).prestation_label || 'Rendez-vous'} — ${fmtDateFR((item as AppointmentCandidate).appointment_date)} ${(item as AppointmentCandidate).start_time}`
        : `${eventClientName(item as EventCandidate) || (item as EventCandidate).type || 'Événement'} — ${fmtDateTimeFR((item as EventCandidate).start_time)}`;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="font-bold text-content flex items-center gap-2"><Link2 size={18} /> Rattacher « {page.title} »</h3>
                        <button onClick={onClose} className="text-content-muted hover:text-content"><X size={18} /></button>
                    </div>
                    <div className="p-5 space-y-4">
                        {currentKind && (
                            <div className="flex items-center justify-between text-sm py-2 px-3 rounded-lg bg-surface-soft">
                                <span className="text-content flex items-center gap-1.5">
                                    {currentKind === 'appointment' ? <CalendarClock size={14} /> : <PartyPopper size={14} />}
                                    {linkDisplay(page)}
                                </span>
                                <button onClick={() => { onUnlink(); onClose(); }} className="text-content-muted hover:text-red-500 text-xs font-bold">Retirer</button>
                            </div>
                        )}
                        <div className="flex gap-1.5">
                            <button onClick={() => setKind('appointment')} className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 ${kind === 'appointment' ? 'bg-[var(--brand-color)] text-white' : 'bg-surface-soft text-content-muted'}`}>
                                <CalendarClock size={13} /> Rendez-vous
                            </button>
                            <button onClick={() => setKind('event')} className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 ${kind === 'event' ? 'bg-[var(--brand-color)] text-white' : 'bg-surface-soft text-content-muted'}`}>
                                <PartyPopper size={13} /> Événement
                            </button>
                        </div>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                                placeholder={kind === 'appointment' ? 'Chercher un rendez-vous…' : 'Chercher un événement…'}
                                className="w-full bg-surface-soft border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-content placeholder:text-content-muted outline-none focus:border-[var(--brand-color)]" />
                        </div>
                        <div className="max-h-56 overflow-y-auto space-y-1">
                            {loading ? (
                                <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={18} /></div>
                            ) : results.length === 0 ? (
                                <p className="text-xs text-content-muted text-center py-4">Aucun résultat.</p>
                            ) : (
                                results.map((r) => (
                                    <button key={r.id} onClick={() => { onLink(kind, r); onClose(); }}
                                        className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-surface-soft text-content truncate">
                                        {labelFor(r, kind)}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

/** I5 : glisser-déposer + clavier (Alt+Haut/Bas) pour réordonner — dans sa propre fratrie
 *  seulement (les pages de premier rang d'une section, ou les sous-pages d'une même page mère),
 *  jamais entre les deux (une sous-page ne peut pas devenir une page de premier rang ici). */
function PageRow({ page, depth, active, isDragging, showDropIndicator, onSelect, onAddSubpage, onDelete, onDragStartPage, onDragOverPage, onDropOnPage, onDragEndPage, onMoveWithinSiblings }: {
    page: Page; depth: number; active: boolean;
    isDragging: boolean; showDropIndicator: boolean;
    onSelect: () => void; onAddSubpage?: () => void; onDelete: () => void;
    onDragStartPage: () => void; onDragOverPage: (e: React.DragEvent) => void; onDropOnPage: () => void; onDragEndPage: () => void;
    onMoveWithinSiblings: (dir: -1 | 1) => void;
}) {
    return (
        <div
            tabIndex={0} onClick={onSelect}
            onDragOver={(e) => { e.preventDefault(); onDragOverPage(e); }}
            onDrop={(e) => { e.preventDefault(); onDropOnPage(); }}
            onKeyDown={(e) => {
                if (!e.altKey) return;
                if (e.key === 'ArrowUp') { e.preventDefault(); onMoveWithinSiblings(-1); }
                else if (e.key === 'ArrowDown') { e.preventDefault(); onMoveWithinSiblings(1); }
            }}
            className={`group flex items-center gap-1.5 py-1.5 px-2 rounded-lg cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] ${active ? 'bg-[var(--brand-color)] text-white' : 'hover:bg-surface-soft text-content'} ${isDragging ? 'opacity-40' : ''} ${showDropIndicator ? 'border-t-2 border-[var(--brand-color)]' : ''}`}
            style={{ marginLeft: depth * 16 }}>
            <span draggable onDragStart={(e) => { e.stopPropagation(); onDragStartPage(); }} onDragEnd={onDragEndPage} onClick={(e) => e.stopPropagation()}
                className={`cursor-grab active:cursor-grabbing shrink-0 ${active ? 'text-white/70' : 'text-content-muted/60 hover:text-content-muted'}`} title="Glisser pour réordonner">
                <GripVertical size={12} />
            </span>
            <FileText size={13} className={active ? 'text-white' : 'text-content-muted'} />
            <span className="text-sm truncate flex-1">{page.title}</span>
            {(page.linked_appointment_id || page.linked_event_id) && (
                <span title={page.linked_appointment_id ? 'Rattachée à un rendez-vous' : 'Rattachée à un événement'}>
                    <Link2 size={11} className={active ? 'text-white' : 'text-content-muted'} />
                </span>
            )}
            {onAddSubpage && (
                <button onClick={(e) => { e.stopPropagation(); onAddSubpage(); }} className={`opacity-0 group-hover:opacity-100 ${active ? 'text-white' : 'text-content-muted hover:text-content'}`} title="Ajouter une sous-page">
                    <Plus size={13} />
                </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className={`opacity-0 group-hover:opacity-100 ${active ? 'text-white' : 'text-content-muted hover:text-red-400'}`}>
                <Trash2 size={13} />
            </button>
        </div>
    );
}

export const NotesModule: React.FC = () => {
    const [notebooks, setNotebooks] = React.useState<Notebook[]>([]);
    const [activeNotebookId, setActiveNotebookId] = React.useState<string | null>(null);
    const [sections, setSections] = React.useState<Section[]>([]);
    const [pages, setPages] = React.useState<Page[]>([]);
    const [activeSectionId, setActiveSectionId] = React.useState<string | null>(null);
    const [activePageId, setActivePageId] = React.useState<string | null>(null);
    const [pageContent, setPageContent] = React.useState<any>(null);
    const [loading, setLoading] = React.useState(true);
    const [loadingPage, setLoadingPage] = React.useState(false);
    const [saveStatus, setSaveStatus] = React.useState<'idle' | 'saving' | 'saved'>('idle');
    const [sharingNotebook, setSharingNotebook] = React.useState<Notebook | null>(null);
    const [creatingNotebook, setCreatingNotebook] = React.useState(false);
    const [creatingSection, setCreatingSection] = React.useState(false);
    const [creatingPageFor, setCreatingPageFor] = React.useState<{ sectionId?: string; parentPageId?: string } | null>(null);
    const [editingTitle, setEditingTitle] = React.useState(false);
    const [titleDraft, setTitleDraft] = React.useState('');
    const [linkingPage, setLinkingPage] = React.useState<Page | null>(null);

    // I5 : réordonner sections/pages/sous-pages, à la souris comme au clavier.
    const [draggingSectionId, setDraggingSectionId] = React.useState<string | null>(null);
    const [sectionDragOverIndex, setSectionDragOverIndex] = React.useState<number | null>(null);
    const [draggingPageId, setDraggingPageId] = React.useState<string | null>(null);
    const [pageDragOverInfo, setPageDragOverInfo] = React.useState<{ siblingKey: string; index: number } | null>(null);

    const saveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const draftRef = React.useRef<any>(null);

    const loadNotebooks = React.useCallback(() => {
        return notesApi.getNotebooks().then((r: any) => {
            const nb: Notebook[] = r.notebooks || [];
            setNotebooks(nb);
            setActiveNotebookId((prev) => (prev && nb.some((n) => n.id === prev)) ? prev : (nb[0]?.id ?? null));
            return nb;
        });
    }, []);

    React.useEffect(() => { loadNotebooks().finally(() => setLoading(false)); }, [loadNotebooks]);

    const openNotebook = React.useCallback((id: string) => {
        setActiveNotebookId(id);
        setActivePageId(null);
        setPageContent(null);
        notesApi.getNotebookContent(id).then((r: any) => {
            const s: Section[] = r.sections || [];
            setSections(s);
            setPages(r.pages || []);
            setActiveSectionId((prev) => (prev && s.some((x) => x.id === prev)) ? prev : (s[0]?.id ?? null));
        });
    }, []);

    React.useEffect(() => { if (activeNotebookId) openNotebook(activeNotebookId); }, [activeNotebookId]); // eslint-disable-line react-hooks/exhaustive-deps

    const openPage = (id: string) => {
        setActivePageId(id);
        setLoadingPage(true);
        setEditingTitle(false);
        notesApi.getPageContent(id).then((r: any) => setPageContent(r.content)).finally(() => setLoadingPage(false));
    };

    const scheduleSave = (content: any) => {
        draftRef.current = content;
        setSaveStatus('saving');
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            if (!activePageId) return;
            await notesApi.savePageContent(activePageId, draftRef.current);
            setSaveStatus('saved');
        }, SAVE_DEBOUNCE_MS);
    };

    // Enregistre immédiatement en quittant la page — sinon les dernières frappes avant de
    // changer de page seraient perdues (le minuteur n'aurait pas eu le temps de se déclencher).
    React.useEffect(() => {
        return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    }, [activePageId]);

    const handleCreateNotebook = async (name: string) => {
        const r: any = await notesApi.createNotebook(name);
        await loadNotebooks();
        setActiveNotebookId(r.notebook.id);
    };
    const handleDeleteNotebook = async (id: string) => {
        if (!window.confirm('Supprimer ce bloc-notes, avec toutes ses sections et pages ? Cette action est définitive.')) return;
        await notesApi.deleteNotebook(id);
        loadNotebooks();
    };
    const handleCreateSection = async (name: string) => {
        if (!activeNotebookId) return;
        const r: any = await notesApi.createSection(activeNotebookId, name);
        setSections((prev) => [...prev, r.section]);
        setActiveSectionId(r.section.id);
    };
    const handleDeleteSection = async (id: string) => {
        if (!window.confirm('Supprimer cette section et toutes ses pages ? Cette action est définitive.')) return;
        await notesApi.deleteSection(id);
        setSections((prev) => prev.filter((s) => s.id !== id));
        setPages((prev) => prev.filter((p) => p.section_id !== id));
        if (activeSectionId === id) setActiveSectionId(null);
    };
    const handleCreatePage = async (title: string) => {
        if (!creatingPageFor) return;
        const r: any = await notesApi.createPage({ ...creatingPageFor, title });
        setPages((prev) => [...prev, r.page]);
        setCreatingPageFor(null);
        openPage(r.page.id);
    };
    const handleDeletePage = async (id: string) => {
        if (!window.confirm('Supprimer cette page ? Cette action est définitive.')) return;
        await notesApi.deletePage(id);
        setPages((prev) => prev.filter((p) => p.id !== id && p.parent_page_id !== id));
        if (activePageId === id) { setActivePageId(null); setPageContent(null); }
    };
    const handleRenameTitle = async () => {
        if (!activePageId || !titleDraft.trim()) { setEditingTitle(false); return; }
        const r: any = await notesApi.renamePage(activePageId, titleDraft.trim());
        setPages((prev) => prev.map((p) => p.id === activePageId ? { ...p, ...r.page } : p));
        setEditingTitle(false);
    };
    // Le PATCH ne renvoie que les identifiants de rattachement, pas les libellés lisibles (ils
    // viennent d'une jointure faite uniquement pour la liste des pages) — on a déjà l'objet choisi
    // sous la main dans LinkDialog, donc on construit le libellé localement plutôt que de tout
    // recharger (ce qui réinitialiserait la page ouverte, comme `openNotebook`).
    const handleLinkPage = async (kind: 'appointment' | 'event', item: AppointmentCandidate | EventCandidate) => {
        if (!linkingPage) return;
        if (kind === 'appointment') {
            const a = item as AppointmentCandidate;
            await notesApi.linkPageToAppointment(linkingPage.id, a.id);
            setPages((prev) => prev.map((p) => p.id === linkingPage.id
                ? { ...p, linked_appointment_id: a.id, linked_event_id: null, linked_appointment_label: a.prestation_label, linked_appointment_date: a.appointment_date, linked_appointment_time: a.start_time }
                : p));
        } else {
            const e = item as EventCandidate;
            await notesApi.linkPageToEvent(linkingPage.id, e.id);
            setPages((prev) => prev.map((p) => p.id === linkingPage.id
                ? { ...p, linked_event_id: e.id, linked_appointment_id: null, linked_event_label: eventClientName(e) || e.type, linked_event_type: e.type, linked_event_start: e.start_time }
                : p));
        }
    };
    const handleUnlinkPage = async () => {
        if (!linkingPage) return;
        await notesApi.linkPageToAppointment(linkingPage.id, null);
        setPages((prev) => prev.map((p) => p.id === linkingPage.id
            ? { ...p, linked_appointment_id: null, linked_event_id: null }
            : p));
    };

    // I5 : réordonner les sections d'un bloc-notes.
    const handleMoveSection = async (id: string, index: number) => {
        const r: any = await notesApi.moveSection(id, index);
        setSections(r.sections || []);
    };
    const handleDragStartSection = (id: string) => setDraggingSectionId(id);
    const handleDragOverSectionSlot = (index: number) => setSectionDragOverIndex(index);
    const handleDragEndSection = () => { setDraggingSectionId(null); setSectionDragOverIndex(null); };
    const handleDropSection = () => {
        if (draggingSectionId && sectionDragOverIndex !== null) handleMoveSection(draggingSectionId, sectionDragOverIndex);
        handleDragEndSection();
    };

    // I5 : réordonner pages et sous-pages — le serveur (freres/insertAt) borne le calcul à la
    // bonne fratrie (pages de premier rang d'une section, ou sous-pages d'une même page mère) et
    // ne renvoie QUE cette fratrie ; on la fusionne dans `pages` sans toucher au reste.
    const handleMovePage = async (id: string, index: number) => {
        const r: any = await notesApi.movePage(id, index);
        const fratrie: Page[] = r.pages || [];
        const idsFratrie = new Set(fratrie.map((p) => p.id));
        setPages((prev) => [...prev.filter((p) => !idsFratrie.has(p.id)), ...fratrie]);
    };
    const handleDragStartPage = (id: string) => setDraggingPageId(id);
    const handleDragOverPageSlot = (siblingKey: string, index: number) => setPageDragOverInfo({ siblingKey, index });
    const handleDragEndPage = () => { setDraggingPageId(null); setPageDragOverInfo(null); };
    const handleDropPage = () => {
        if (draggingPageId && pageDragOverInfo) handleMovePage(draggingPageId, pageDragOverInfo.index);
        handleDragEndPage();
    };

    const activeNotebook = notebooks.find((n) => n.id === activeNotebookId) || null;
    const activePage = pages.find((p) => p.id === activePageId) || null;
    const sectionPages = pages.filter((p) => p.section_id === activeSectionId && p.parent_page_id === null);
    const subPagesOf = (parentId: string) => pages.filter((p) => p.parent_page_id === parentId);

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={28} /></div>;

    return (
        <div className="space-y-6">
            <header>
                <h1 className="text-2xl font-bold tracking-tight text-content flex items-center gap-2"><NotebookText size={22} /> Notes</h1>
                <p className="text-content-muted mt-1 text-sm">Vos bloc-notes, privés ou partagés avec vos collègues.</p>
            </header>

            <div className="flex gap-2 flex-wrap items-center">
                {notebooks.map((n) => (
                    <button key={n.id} onClick={() => setActiveNotebookId(n.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all flex items-center gap-1.5 ${activeNotebookId === n.id ? 'bg-[var(--brand-color)] text-white border-transparent' : 'border-border text-content-muted hover:text-content'}`}>
                        {n.name}{!n.mine && <Users size={11} />}
                    </button>
                ))}
                <button onClick={() => setCreatingNotebook(true)} className="px-3 py-1.5 rounded-full text-xs font-bold border border-dashed border-border text-content-muted hover:text-content flex items-center gap-1">
                    <Plus size={13} /> Nouveau bloc-notes
                </button>
            </div>

            {notebooks.length === 0 && (
                <div className="text-center py-16 bg-surface rounded-2xl border border-dashed border-border">
                    <p className="text-content-muted">Aucun bloc-notes pour l'instant.</p>
                    <button onClick={() => setCreatingNotebook(true)} className="mt-3 px-4 py-2 rounded-lg bg-[var(--brand-color)] text-white text-sm font-bold">Créer mon premier bloc-notes</button>
                </div>
            )}

            {activeNotebook && (
                <div className="grid md:grid-cols-[280px_1fr] gap-4">
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            {activeNotebook.mine && (
                                <button onClick={() => setSharingNotebook(activeNotebook)} className="text-[11px] font-bold text-content-muted hover:text-content flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border">
                                    <Users size={12} /> Partager
                                </button>
                            )}
                            {activeNotebook.mine && (
                                <button onClick={() => handleDeleteNotebook(activeNotebook.id)} className="text-[11px] font-bold text-content-muted hover:text-red-500 flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border">
                                    <Trash2 size={12} /> Supprimer
                                </button>
                            )}
                        </div>

                        {/* I5 : sections réordonnables par glisser-déposer (la section elle-même
                            sert de poignée — pas de place pour une poignée séparée dans une pastille)
                            et au clavier (Alt+Gauche/Droite, focus sur la section). */}
                        <div className="flex gap-1.5 flex-wrap items-center">
                            {(() => {
                                let slot = 0;
                                return sections.map((s, i) => {
                                    const isDraggedSection = draggingSectionId === s.id;
                                    const slotBefore = slot;
                                    if (!isDraggedSection) slot++;
                                    return (
                                        <button key={s.id} tabIndex={0}
                                            draggable onDragStart={() => handleDragStartSection(s.id)} onDragEnd={handleDragEndSection}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                if (isDraggedSection) return;
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                const before = e.clientX < rect.left + rect.width / 2;
                                                handleDragOverSectionSlot(before ? slotBefore : slotBefore + 1);
                                            }}
                                            onDrop={(e) => { e.preventDefault(); handleDropSection(); }}
                                            onKeyDown={(e) => {
                                                if (!e.altKey) return;
                                                if (e.key === 'ArrowLeft') { e.preventDefault(); if (i > 0) handleMoveSection(s.id, slotBefore - 1); }
                                                else if (e.key === 'ArrowRight') { e.preventDefault(); if (i < sections.length - 1) handleMoveSection(s.id, slotBefore + 1); }
                                            }}
                                            onClick={() => setActiveSectionId(s.id)}
                                            className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-grab active:cursor-grabbing outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] ${activeSectionId === s.id ? 'bg-[var(--brand-color)] text-white' : 'bg-surface-soft text-content-muted hover:text-content'} ${isDraggedSection ? 'opacity-40' : ''} ${sectionDragOverIndex === slotBefore && draggingSectionId && !isDraggedSection ? 'ring-2 ring-[var(--brand-color)]' : ''}`}>
                                            {s.name}
                                        </button>
                                    );
                                });
                            })()}
                            <button onClick={() => setCreatingSection(true)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-dashed border-border text-content-muted hover:text-content">
                                <Plus size={12} className="inline" /> Section
                            </button>
                        </div>

                        {activeSectionId && (
                            <div className="bg-surface border border-border rounded-2xl p-2 space-y-0.5">
                                {(() => {
                                    const sectionKey = `section:${activeSectionId}`;
                                    let slot = 0;
                                    return sectionPages.map((p) => {
                                        const isDraggedRow = draggingPageId === p.id;
                                        const slotBefore = slot;
                                        if (!isDraggedRow) slot++;
                                        const subPages = subPagesOf(p.id);
                                        const pageKey = `page:${p.id}`;
                                        let subSlot = 0;
                                        return (
                                            <React.Fragment key={p.id}>
                                                <PageRow page={p} depth={0} active={activePageId === p.id}
                                                    isDragging={isDraggedRow}
                                                    showDropIndicator={!!draggingPageId && pageDragOverInfo?.siblingKey === sectionKey && pageDragOverInfo?.index === slotBefore}
                                                    onSelect={() => openPage(p.id)}
                                                    onAddSubpage={() => setCreatingPageFor({ parentPageId: p.id })}
                                                    onDelete={() => handleDeletePage(p.id)}
                                                    onDragStartPage={() => handleDragStartPage(p.id)}
                                                    onDragOverPage={(e) => {
                                                        if (isDraggedRow) return;
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        const before = e.clientY < rect.top + rect.height / 2;
                                                        handleDragOverPageSlot(sectionKey, before ? slotBefore : slotBefore + 1);
                                                    }}
                                                    onDropOnPage={handleDropPage}
                                                    onDragEndPage={handleDragEndPage}
                                                    onMoveWithinSiblings={(dir) => handleMovePage(p.id, slotBefore + dir)}
                                                />
                                                {subPages.map((sp) => {
                                                    const isDraggedSub = draggingPageId === sp.id;
                                                    const subSlotBefore = subSlot;
                                                    if (!isDraggedSub) subSlot++;
                                                    return (
                                                        <PageRow key={sp.id} page={sp} depth={1} active={activePageId === sp.id}
                                                            isDragging={isDraggedSub}
                                                            showDropIndicator={!!draggingPageId && pageDragOverInfo?.siblingKey === pageKey && pageDragOverInfo?.index === subSlotBefore}
                                                            onSelect={() => openPage(sp.id)}
                                                            onDelete={() => handleDeletePage(sp.id)}
                                                            onDragStartPage={() => handleDragStartPage(sp.id)}
                                                            onDragOverPage={(e) => {
                                                                if (isDraggedSub) return;
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                const before = e.clientY < rect.top + rect.height / 2;
                                                                handleDragOverPageSlot(pageKey, before ? subSlotBefore : subSlotBefore + 1);
                                                            }}
                                                            onDropOnPage={handleDropPage}
                                                            onDragEndPage={handleDragEndPage}
                                                            onMoveWithinSiblings={(dir) => handleMovePage(sp.id, subSlotBefore + dir)}
                                                        />
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    });
                                })()}
                                <button onClick={() => setCreatingPageFor({ sectionId: activeSectionId })}
                                    className="w-full flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-content-muted hover:text-content hover:bg-surface-soft text-sm">
                                    <Plus size={13} /> Nouvelle page
                                </button>
                                {activeSectionId && (
                                    <button onClick={() => handleDeleteSection(activeSectionId)} className="w-full text-left text-[11px] text-content-muted hover:text-red-500 px-2 pt-1">
                                        Supprimer la section
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="bg-surface border border-border rounded-2xl p-5 min-h-[300px]">
                        {!activePage ? (
                            <div className="flex items-center justify-center h-full text-content-muted text-sm py-16">
                                <ChevronRight size={16} className="mr-1" /> Choisissez une page, ou créez-en une.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    {editingTitle ? (
                                        <input autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)}
                                            onBlur={handleRenameTitle}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                                            className="text-xl font-bold text-content bg-transparent outline-none border-b border-[var(--brand-color)] flex-1" />
                                    ) : (
                                        <h2 onClick={() => { setTitleDraft(activePage.title); setEditingTitle(true); }}
                                            className="text-xl font-bold text-content cursor-text flex-1">{activePage.title}</h2>
                                    )}
                                    <span className="text-[11px] text-content-muted flex items-center gap-1 shrink-0">
                                        {saveStatus === 'saving' && <><Loader2 size={11} className="animate-spin" /> Enregistrement…</>}
                                        {saveStatus === 'saved' && <><Check size={11} className="text-emerald-500" /> Enregistré</>}
                                    </span>
                                </div>
                                <div>
                                    {(activePage.linked_appointment_id || activePage.linked_event_id) ? (
                                        <button onClick={() => setLinkingPage(activePage)}
                                            className="text-[11px] font-bold text-content-muted hover:text-content flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border">
                                            {activePage.linked_appointment_id ? <CalendarClock size={12} /> : <PartyPopper size={12} />}
                                            {linkDisplay(activePage)}
                                        </button>
                                    ) : (
                                        <button onClick={() => setLinkingPage(activePage)}
                                            className="text-[11px] font-bold text-content-muted hover:text-content flex items-center gap-1.5 px-2 py-1 rounded-lg border border-dashed border-border">
                                            <Link2 size={12} /> Rattacher à un rendez-vous ou un événement
                                        </button>
                                    )}
                                </div>
                                {loadingPage ? (
                                    <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
                                ) : (
                                    <React.Suspense fallback={<div className="text-sm text-content-muted py-8 text-center">Chargement de l'éditeur…</div>}>
                                        <RichEditor key={activePageId} content={pageContent} onChange={scheduleSave} />
                                    </React.Suspense>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {creatingNotebook && (
                <PromptDialog title="Nouveau bloc-notes" placeholder="Nom du bloc-notes" confirmLabel="Créer le bloc-notes"
                    onSubmit={handleCreateNotebook} onClose={() => setCreatingNotebook(false)} />
            )}
            {creatingSection && (
                <PromptDialog title="Nouvelle section" placeholder="Nom de la section" confirmLabel="Créer la section"
                    onSubmit={handleCreateSection} onClose={() => setCreatingSection(false)} />
            )}
            {creatingPageFor && (
                <PromptDialog title={creatingPageFor.parentPageId ? 'Nouvelle sous-page' : 'Nouvelle page'} placeholder="Titre de la page" confirmLabel="Créer la page"
                    onSubmit={handleCreatePage} onClose={() => setCreatingPageFor(null)} />
            )}
            {sharingNotebook && (
                <ShareDialog notebookId={sharingNotebook.id} name={sharingNotebook.name} onClose={() => setSharingNotebook(null)} />
            )}
            {linkingPage && (
                <LinkDialog page={linkingPage} onLink={handleLinkPage} onUnlink={handleUnlinkPage} onClose={() => setLinkingPage(null)} />
            )}
        </div>
    );
};

export default NotesModule;
